import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Euro as EuroIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  getUserPurchases, 
  downloadInvoice, 
  getPurchaseById,
  Purchase,
  PurchaseType,
  PaymentStatus,
  getPaymentStatusLabel,
  getPaymentStatusColor,
  formatDate,
  getPurchaseTypeLabel
} from '../services/professionService';

// ==============================================
// STYLES PERSONNALISÉS AVEC COULEURS #4bb0c0 ET #f4a261
// ==============================================


interface ClientsPurchasesTableProps {
  userRole?: string; // 'ADMIN' ou 'CLIENT'
}


const StyledTableContainer = styled(TableContainer)({
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  '& .MuiTableCell-head': {
    backgroundColor: '#4bb0c0',
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: '0.9rem'
  },
  '& .MuiTableCell-body': {
    fontSize: '0.85rem'
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: 'rgba(75, 176, 192, 0.08)',
    cursor: 'pointer'
  }
});

const StatusChip = styled(Chip)<{ statuscolor: string }>(({ statuscolor }) => {
  const colors: Record<string, { bg: string; color: string }> = {
    success: { bg: '#4bb0c0', color: '#ffffff' },
    warning: { bg: '#f4a261', color: '#ffffff' },
    error: { bg: '#f4a261', color: '#ffffff' },
    default: { bg: '#e0e0e0', color: '#666666' }
  };
  const style = colors[statuscolor] || colors.default;
  
  return {
    backgroundColor: style.bg,
    color: style.color,
    fontWeight: 'bold',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '8px'
    }
  };
});

const StatsCard = styled(Card)({
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s',
  borderTop: '3px solid #4bb0c0',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 12px rgba(75, 176, 192, 0.2)'
  }
});

const PrimaryButton = styled(Button)({
  backgroundColor: '#4bb0c0',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#3a8c9a'
  }
});

const SecondaryButton = styled(Button)({
  backgroundColor: '#f4a261',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#d48a4e'
  }
});

const OutlinedButton = styled(Button)({
  borderColor: '#4bb0c0',
  color: '#4bb0c0',
  '&:hover': {
    borderColor: '#3a8c9a',
    backgroundColor: 'rgba(75, 176, 192, 0.04)'
  }
});

// ==============================================
// TYPES
// ==============================================

interface PurchaseWithUser extends Purchase {
  user?: {
    id: number;
    email: string;
    firstname: string;
    name: string;
  };
}

interface PurchaseStats {
  total: number;
  totalAmount: number;
  premiumCount: number;
  exportCount: number;
}

// ==============================================
// COMPOSANT PRINCIPAL
// ==============================================

const ClientsPurchasesTable: React.FC<ClientsPurchasesTableProps> = ({ userRole }) => {
  const [purchases, setPurchases] = useState<PurchaseWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filtres
  const [filters, setFilters] = useState({
    type: '' as PurchaseType | '',
    startDate: '',
    endDate: ''
  });
  
  // Statistiques
  const [stats, setStats] = useState<PurchaseStats>({
    total: 0,
    totalAmount: 0,
    premiumCount: 0,
    exportCount: 0
  });
  
  // Dialog pour les détails
  const [selectedPurchase, setSelectedPurchase] = useState<PurchaseWithUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

// ==============================================
// CHARGEMENT DES DONNÉES
// ==============================================

// ==============================================
// CHARGEMENT DES DONNÉES
// ==============================================

const loadPurchases = async () => {
  setLoading(true);
  setError(null);
  
  try {
    const role = userRole || localStorage.getItem('role');
    let userId: number | null = null;
    
    if (role === 'ADMIN') {
      userId = 0;
    } else {
      const storedUserId = localStorage.getItem('userId');
      userId = storedUserId ? parseInt(storedUserId, 10) : null;
    }
    
    if (userId === null) {
      setError("Utilisateur non identifié");
      setLoading(false);
      return;
    }
    
    const response = await getUserPurchases(userId, {
      type: filters.type || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      limit,
      offset: (page - 1) * limit
    });
    
    console.log("Achats chargés :", response.data);
    
    setPurchases(response.data.purchases);
    setTotalItems(response.data.total);
    setTotalPages(response.data.pagination.totalPages);
    
    // Calculer les statistiques
    const calculatedStats = response.data.purchases.reduce((acc: PurchaseStats, purchase: Purchase) => {
      acc.total++;
      acc.totalAmount += Number(purchase.amount);
      if (purchase.purchase_type === 'premium') acc.premiumCount++;
      if (purchase.purchase_type === 'export') acc.exportCount++;
      return acc;
    }, { total: 0, totalAmount: 0, premiumCount: 0, exportCount: 0 });
    
    setStats({
      ...calculatedStats,
      totalAmount: response.data.totalAmount || 0
    });
    
  } catch (err: any) {
    setError(err.message || "Erreur lors du chargement des achats");
    console.error("Erreur loadPurchases:", err);
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    loadPurchases();
  }, [page, filters]);

  // ==============================================
  // GESTION DES FILTRES
  // ==============================================

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ type: '', startDate: '', endDate: '' });
    setPage(1);
  };

  // ==============================================
  // GESTION DES ACTIONS
  // ==============================================

  const handleViewDetails = async (purchaseId: number) => {
    setDetailsLoading(true);
    try {
      const purchase = await getPurchaseById(purchaseId);
      setSelectedPurchase(purchase);
      setDialogOpen(true);
    } catch (err: any) {
      setError(err.message || "Erreur lors du chargement des détails");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDownloadInvoice = async (purchaseId: number) => {
    try {
      await downloadInvoice(purchaseId);
      setSuccess("Facture téléchargée avec succès");
    } catch (err: any) {
      setError(err.message || "Erreur lors du téléchargement de la facture");
    }
  };

  // ==============================================
  // RENDU DU TABLEAU
  // ==============================================

  if (loading && purchases.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#4bb0c0' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête */}
      <Typography variant="h4" gutterBottom sx={{ mb: 3, color: '#4bb0c0', fontWeight: 'bold' }}>
        Historique des achats clients
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total achats
                  </Typography>
                  <Typography variant="h5" component="div" sx={{ color: '#4bb0c0', fontWeight: 'bold' }}>
                    {stats.total}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: '#4bb0c0', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total achat (en €)
                  </Typography>
                  <Typography variant="h5" component="div" sx={{ color: '#f4a261', fontWeight: 'bold' }}>
                    {stats.totalAmount.toFixed(2)} €
                  </Typography>
                </Box>
                <EuroIcon sx={{ fontSize: 40, color: '#f4a261', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Places Premium
                  </Typography>
                  <Typography variant="h5" component="div" sx={{ color: '#4bb0c0', fontWeight: 'bold' }}>
                    {stats.premiumCount}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: '#4bb0c0', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Exports
                  </Typography>
                  <Typography variant="h5" component="div" sx={{ color: '#f4a261', fontWeight: 'bold' }}>
                    {stats.exportCount}
                  </Typography>
                </Box>
                <WorkIcon sx={{ fontSize: 40, color: '#f4a261', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FilterIcon sx={{ color: '#4bb0c0' }} />
          <Typography variant="subtitle1" sx={{ color: '#4bb0c0', fontWeight: 'bold' }}>
            Filtres :
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: '#4bb0c0' }}>Type d'achat</InputLabel>
            <Select
              value={filters.type}
              label="Type d'achat"
              onChange={(e) => handleFilterChange('type', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4bb0c0'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f4a261'
                }
              }}
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="premium">Premium</MenuItem>
              <MenuItem value="export">Export</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Date début"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4bb0c0'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#f4a261'
              }
            }}
          />

          <TextField
            size="small"
            type="date"
            label="Date fin"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4bb0c0'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#f4a261'
              }
            }}
          />

          <OutlinedButton
            startIcon={<RefreshIcon />}
            onClick={resetFilters}
            size="small"
          >
            Réinitialiser
          </OutlinedButton>
        </Box>
      </Paper>

      {/* Tableau des achats */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Montant</TableCell>
              <TableCell>Profession</TableCell>
              <TableCell>Date d'achat</TableCell>
              <TableCell align="center">Facture</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    Aucun achat trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((purchase) => (
                <TableRow key={purchase.id} onClick={() => handleViewDetails(purchase.id)}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#4bb0c0' }}>
                        {purchase.user?.firstname} {purchase.user?.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {purchase.user_id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{purchase.user?.email || 'N/A'}</TableCell>
                    <TableCell>
                    <Chip
                        label={getPurchaseTypeLabel(purchase.purchase_type)}
                        size="small"
                        sx={{
                        backgroundColor: purchase.purchase_type === 'premium' ? '#4bb0c0' : '#f4a261',
                        color: '#ffffff !important',
                        fontWeight: 'bold',
                        '& .MuiChip-label': {
                            color: '#ffffff !important'
                        }
                        }}
                    />
                    </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" sx={{ color: '#f4a261' }}>
                      {Number(purchase.amount).toFixed(2)} €
                    </Typography>
                  </TableCell>
                  <TableCell>{purchase.profession_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Tooltip title={new Date(purchase.purchased_at).toLocaleString()}>
                      <span>{formatDate(purchase.purchased_at)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {purchase.invoice_pdf_path ? (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadInvoice(purchase.id);
                        }}
                        size="small"
                        sx={{
                          color: '#4bb0c0',
                          '&:hover': {
                            color: '#f4a261'
                          }
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Non disponible
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(purchase.id);
                      }}
                      size="small"
                      sx={{
                        color: '#f4a261',
                        '&:hover': {
                          color: '#4bb0c0'
                        }
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#4bb0c0',
                '&.Mui-selected': {
                  backgroundColor: '#4bb0c0',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#3a8c9a'
                  }
                }
              }
            }}
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Dialog des détails */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: '4px solid #4bb0c0'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon sx={{ color: '#4bb0c0' }} />
            <Typography variant="h6" sx={{ color: '#4bb0c0', fontWeight: 'bold' }}>
              Détails de l'achat
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress sx={{ color: '#4bb0c0' }} />
            </Box>
          ) : selectedPurchase && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Client
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ color: '#4bb0c0', fontWeight: 'bold' }}>
                  {selectedPurchase.user?.firstname} {selectedPurchase.user?.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedPurchase.user?.email}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Numéro de facture
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {selectedPurchase.invoice_number}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Type d'achat
                </Typography>
                <Chip
                  label={getPurchaseTypeLabel(selectedPurchase.purchase_type)}
                  size="small"
                  sx={{
                    backgroundColor: selectedPurchase.purchase_type === 'premium' ? '#4bb0c0' : '#f4a261',
                    color: '#ffffff',
                    fontWeight: 'bold'
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Montant
                </Typography>
                <Typography variant="h6" sx={{ color: '#f4a261', fontWeight: 'bold' }}>
                  {Number(selectedPurchase.amount).toFixed(2)} €
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Date d'achat
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedPurchase.purchased_at).toLocaleString('fr-FR')}
                </Typography>
              </Grid>

              {selectedPurchase.profession_name && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Profession
                  </Typography>
                  <Typography variant="body1">
                    {selectedPurchase.profession_name}
                  </Typography>
                </Grid>
              )}

              {selectedPurchase.premium_position && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Position Premium
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#4bb0c0', fontWeight: 'bold' }}>
                    #{selectedPurchase.premium_position}
                  </Typography>
                </Grid>
              )}

              {selectedPurchase.export_file_name && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fichier exporté
                  </Typography>
                  <Typography variant="body1">
                    {selectedPurchase.export_file_name}
                  </Typography>
                  {selectedPurchase.export_record_count && (
                    <Typography variant="caption" color="textSecondary">
                      {selectedPurchase.export_record_count} enregistrements
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPurchase?.invoice_pdf_path && (
            <PrimaryButton
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadInvoice(selectedPurchase.id)}
            >
              Télécharger la facture
            </PrimaryButton>
          )}
          <SecondaryButton onClick={() => setDialogOpen(false)}>
            Fermer
          </SecondaryButton>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ 
            backgroundColor: '#f4a261',
            color: '#ffffff',
            '& .MuiAlert-icon': { color: '#ffffff' }
          }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity="success" 
          onClose={() => setSuccess(null)}
          sx={{ 
            backgroundColor: '#4bb0c0',
            color: '#ffffff',
            '& .MuiAlert-icon': { color: '#ffffff' }
          }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientsPurchasesTable;